import React from "react";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contacts } from "@/src/gql/graphql";
import { useDetailActionContextOptional } from "@/contexts/DetailActionContext";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { useContactQuickBooksData } from "@/features/companies/hooks/company-quickbooks-hooks";
import type { QuickBooksCustomerWithRealm } from "@/lib/types/quickbooks-unified";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { QuickBooksActions } from "@/components/quickbooks/connection/QuickBooksActions";
import { QuickBooksInvoiceViewer } from "@/components/quickbooks/invoice/QuickBooksInvoiceViewer";

// ============================================================================
// QuickBooksCustomerConnections — inner display card
// ============================================================================

interface QuickBooksCustomerConnectionsProps {
  contact: Contacts;
}

const QuickBooksCustomerConnections: React.FC<QuickBooksCustomerConnectionsProps> = ({
  contact,
}) => {
  const { data: qbData, isLoading, error } = useContactQuickBooksData(contact.id);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">QuickBooks Connections</CardTitle>
          <CardDescription>Loading QuickBooks data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (error || !qbData?.customerData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">QuickBooks Connections</CardTitle>
          <CardDescription>No QuickBooks customers connected</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const customerData = (qbData.customerData || []) as QuickBooksCustomerWithRealm[];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          QuickBooks Connections ({customerData.length})
        </CardTitle>
        <CardDescription>Connected QuickBooks customers from multiple realms</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {customerData.map((customer, index) => (
            <div
              key={`${customer.Id}-${index}`}
              className="flex items-center justify-between p-3 border rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium break-words md:break-normal">{customer.Name || "QuickBooks Customer"}</div>
                <div className="text-sm text-muted-foreground break-words md:break-normal">
                  Customer ID: {customer.Id}
                  {customer.realmId && (
                    <span className="ml-2">
                      <Badge variant="outline" className="text-xs">
                        Realm: {customer.realmId}
                      </Badge>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// ContactQuickBooksDisplay — tab entry point
// ============================================================================

export const ContactQuickBooksDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
  onRefresh,
}) => {
  const actionContext = useDetailActionContextOptional();
  const contextRefresh = actionContext?.onRefresh;
  const refreshCallback = onRefresh || contextRefresh;

  const { isAdmin } = useRoleCheck();
  const { data: qbData } = useContactQuickBooksData(contact.id);

  void refreshCallback; // available for future use

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center py-10 md:py-12 px-4 md:px-0">
        <div className="text-center">
          <h3 className="text-base md:text-lg font-semibold text-card-foreground mb-2">Access Restricted</h3>
          <p className="text-gray-600 dark:text-gray-400">
            You need admin privileges to view QuickBooks information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <QuickBooksCustomerConnections contact={contact} />
      <QuickBooksActions
        qbCustomerId={qbData?.quickbooksCustomerRef || undefined}
        qbDisplayName={qbData?.quickbooksCustomerName || contact.company_name || contact.name}
        contactId={contact.id}
        companyId={qbData?.companyId}
        companyName={contact.company_name || undefined}
      />
      <QuickBooksInvoiceViewer
        contactId={contact.id}
        customerName={contact.company_name || contact.name}
      />
    </div>
  );
};
